const DEFAULT_MEASUREMENT_ID = "G-Z0CBV1SM2Y";
const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID || DEFAULT_MEASUREMENT_ID;
const SCRIPT_ID = "ga-gtag-script";
let initialized = false;
let consentGranted = false;
let lastTrackedPath = "";
let lastTrackedAt = 0;

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

function upsertGtag() {
	window.dataLayer = window.dataLayer || [];
	window.gtag =
		window.gtag ||
		// Google gtag queue requires pushing the native `arguments` object.
		function gtag() {
			// eslint-disable-next-line prefer-rest-params
			window.dataLayer?.push(arguments);
		};
}

function ensureScriptLoaded() {
	if (document.getElementById(SCRIPT_ID)) return;
	const script = document.createElement("script");
	script.id = SCRIPT_ID;
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
	document.head.appendChild(script);
}

function isLocalHost() {
	if (typeof window === "undefined") return false;
	const host = window.location.hostname;
	return host === "localhost" || host === "127.0.0.1" || host === "::1";
}

export function applyAnalyticsConsent(consent: "accepted" | "rejected" | null) {
	if (isLocalHost()) return;
	if (!measurementId) return;
	upsertGtag();
	ensureScriptLoaded();

	if (!initialized) {
		window.gtag?.("js", new Date());
		window.gtag?.("consent", "default", {
			analytics_storage: "denied",
			ad_storage: "denied",
			ad_user_data: "denied",
			ad_personalization: "denied",
			functionality_storage: "granted",
			security_storage: "granted",
			wait_for_update: 500,
		});
		window.gtag?.("config", measurementId);
		initialized = true;
	}

	const granted = consent === "accepted";
	consentGranted = granted;
	window.gtag?.("consent", "update", {
		analytics_storage: granted ? "granted" : "denied",
		ad_storage: granted ? "granted" : "denied",
		ad_user_data: granted ? "granted" : "denied",
		ad_personalization: granted ? "granted" : "denied",
		functionality_storage: "granted",
		security_storage: "granted",
	});
}

export function trackPageView(path?: string) {
	if (isLocalHost()) return;
	if (!measurementId || !consentGranted) return;
	upsertGtag();
	const resolvedPath = path || window.location.pathname + window.location.search;
	const now = Date.now();
	// Prevent accidental duplicate page_view sends from rapid state updates.
	if (resolvedPath === lastTrackedPath && now - lastTrackedAt < 800) return;
	lastTrackedPath = resolvedPath;
	lastTrackedAt = now;
	window.gtag?.("event", "page_view", {
		page_title: document.title,
		page_location: window.location.href,
		page_path: resolvedPath,
	});
}
